import { Injectable, signal } from '@angular/core';
import { SelectListOption } from '@bmw-ds/components';

import { CountryConfigurationFeature } from '../../../auth/user/user';
import {
  DetailedCountryConfiguration,
  FEATURE_FLAG_GROUP_ORDER,
  FeatureFlagElementViewModel,
  FeatureFlagGroup,
  FeatureFlagGroupDetails,
  FeatureFlagUpdate,
  FeatureFlagViewModel,
} from '../client/model/feature-flag.model';

const ACTIVATED = 'activated';
const DEACTIVATED = 'deactivated';
const DEFAULT = 'default';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagMapper {
  mapToViewModel(input: DetailedCountryConfiguration): FeatureFlagViewModel {
    const result: FeatureFlagViewModel = {};

    for (const countryCode of Object.keys(input)) {
      const settings = input[countryCode];

      result[countryCode] = settings.map((setting) => {
        const storedValueKey =
          setting.storedValue === true ? ACTIVATED : setting.storedValue === false ? DEACTIVATED : DEFAULT;
        return {
          group: this.findGroup(setting.feature),
          feature: setting.feature,
          text: this.buildText(setting.feature),
          options: this.buildOptions(setting.defaultValue),
          unchangedModel: storedValueKey,
          model: signal<string>(storedValueKey),
        };
      });
      result[countryCode].sort(compareFeatureFlagElementViewModel);
    }
    return result;
  }

  private buildOptions(defaultValue: boolean): SelectListOption[] {
    return [
      { id: DEFAULT, label: `Use global setting (currently ${defaultValue ? 'ACTIVATED' : 'DEACTIVATED'})` },
      { id: ACTIVATED, label: 'Country specific setting: ACTIVATED' },
      { id: DEACTIVATED, label: 'Country specific setting: DEACTIVATED' },
    ];
  }

  private buildText(feature: CountryConfigurationFeature): { label: string; description: string } {
    switch (feature) {
      case CountryConfigurationFeature.IDENTITY_TITLE:
        return { label: 'Customer title', description: 'Display and update the customer’s title.' };
      case CountryConfigurationFeature.IDENTITY_SUFFIX:
        return { label: 'Customer name suffix', description: 'Display and update the customer’s name suffix.' };
      case CountryConfigurationFeature.IDENTITY_MIDDLE_NAME:
        return { label: 'Customer middle name', description: 'Display and update the customer’s middle name.' };
      case CountryConfigurationFeature.IDENTITY_TAX_NUMBER:
        return { label: 'Customer tax number', description: 'Display and update the customer’s tax number.' };
      case CountryConfigurationFeature.IDENTITY_NATIONAL_ID:
        return { label: 'Customer national ID', description: 'Display and update the customer’s national ID.' };
      case CountryConfigurationFeature.IDENTITY_NATIONAL_ID_DOCUMENT_TYPE:
        return {
          label: 'Customer ID document type',
          description: 'Display and update the customer’s national ID document type.',
        };
      case CountryConfigurationFeature.ADDRESS_PROVINCE:
        return { label: 'Province', description: 'Display and update the customer’s province (address field).' };
      case CountryConfigurationFeature.DEALER_CONSENT:
        return { label: 'Dealer consent', description: 'Enable the dealer consent section.' };
      case CountryConfigurationFeature.VIN_SEARCH:
        return { label: 'Search by VIN', description: 'Enable the customer search by VIN.' };
      case CountryConfigurationFeature.LICENSE_SEARCH:
        return { label: 'Search by licence plate', description: 'Enable the customer search by licence plate.' };
      case CountryConfigurationFeature.CUSTOMER_INSIGHTS:
        return { label: 'Customer Insights', description: 'Enable the Customer Insights.' };
      case CountryConfigurationFeature.WORKSHOP_CUSTOMER_STATUS:
        return {
          label: 'Set workshop customer-status',
          description: 'Enables the "workshop" customer-status for users other than sales',
        };
      case CountryConfigurationFeature.C2V_REMOVE_VEHICLES:
        return {
          label: 'C2V Remove Vehicles',
          description: 'Enable removing vehicles from a customer (i.e. removing all relations).',
        };
      case CountryConfigurationFeature.C2V_REMOVE_RELATIONS:
        return {
          label: 'C2V Remove Relations',
          description:
            'Enable removing single relations between customers and vehicles. This only applies for customers with multiple relations to that vehicle.',
        };
      case CountryConfigurationFeature.C2V_ADD_VEHICLES:
        return {
          label: 'C2V Add Vehicles',
          description: 'Enable adding additional vehicles to a customer.',
        };
      case CountryConfigurationFeature.C2V_ADD_RELATIONS:
        return {
          label: 'C2V Add Relations',
          description: 'Enable adding additional relations between a customer and their existing vehicle.',
        };
      case CountryConfigurationFeature.C2V_EDIT_LICENSE_PLATE:
        return {
          label: 'C2V Edit License Plate',
          description: 'Enable editing the licence plate of a vehicle.',
        };
    }
  }

  private findGroup(feature: CountryConfigurationFeature): FeatureFlagGroupDetails {
    switch (feature) {
      // PERSONAL DATA (ID)
      case CountryConfigurationFeature.IDENTITY_NATIONAL_ID:
      case CountryConfigurationFeature.IDENTITY_NATIONAL_ID_DOCUMENT_TYPE:
      case CountryConfigurationFeature.IDENTITY_TAX_NUMBER:
        return {
          id: FeatureFlagGroup.PERSONAL_DATA_ID,
          displayText: 'Personal Data - Identification',
        };
      // PERSONAL DATA (NAME)
      case CountryConfigurationFeature.IDENTITY_MIDDLE_NAME:
      case CountryConfigurationFeature.IDENTITY_SUFFIX:
      case CountryConfigurationFeature.IDENTITY_TITLE:
        return {
          id: FeatureFlagGroup.PERSONAL_DATA_NAME,
          displayText: 'Personal Data - Name',
        };
      // PERSONAL DATA (OTHER)
      case CountryConfigurationFeature.ADDRESS_PROVINCE:
        return {
          id: FeatureFlagGroup.PERSONAL_DATA_OTHER,
          displayText: 'Personal Data - Other',
        };
      // SEARCH FIELD
      case CountryConfigurationFeature.LICENSE_SEARCH:
      case CountryConfigurationFeature.VIN_SEARCH:
        return {
          id: FeatureFlagGroup.SEARCH_FIELD,
          displayText: 'Search Field',
        };
      // PROFILE PAGE
      case CountryConfigurationFeature.CUSTOMER_INSIGHTS:
      case CountryConfigurationFeature.DEALER_CONSENT:
        return {
          id: FeatureFlagGroup.PROFILE_PAGE_FEATURES,
          displayText: 'Profile Page Features',
        };
      // RETAIL
      case CountryConfigurationFeature.WORKSHOP_CUSTOMER_STATUS:
        return {
          id: FeatureFlagGroup.RETAIL,
          displayText: 'Retail',
        };
      // C2V
      case CountryConfigurationFeature.C2V_REMOVE_RELATIONS:
      case CountryConfigurationFeature.C2V_REMOVE_VEHICLES:
      case CountryConfigurationFeature.C2V_ADD_VEHICLES:
      case CountryConfigurationFeature.C2V_ADD_RELATIONS:
      case CountryConfigurationFeature.C2V_EDIT_LICENSE_PLATE:
        return {
          id: FeatureFlagGroup.C2V,
          displayText: 'C2V',
        };
      // OTHER
      default:
        feature satisfies never;
        return {
          id: FeatureFlagGroup.OTHER,
          displayText: 'Other',
          description: 'All other features that do not match any of the existing groups',
        };
    }
  }

  mapFromViewModel(viewModelElements: FeatureFlagElementViewModel[]): FeatureFlagUpdate[] {
    return viewModelElements
      .filter((element) => element.unchangedModel !== element.model())
      .map((element) => ({
        feature: element.feature,
        value: element.model() === ACTIVATED ? true : element.model() === DEACTIVATED ? false : null,
      }));
  }
}

/**
 * Compares FeatureFlagElementViewModel items so they can be sorted.
 * @param a Element to compare
 * @param b Element to compare
 */
export function compareFeatureFlagElementViewModel(a: FeatureFlagElementViewModel, b: FeatureFlagElementViewModel) {
  const iOfA = FEATURE_FLAG_GROUP_ORDER.indexOf(a.group.id);
  const iOfB = FEATURE_FLAG_GROUP_ORDER.indexOf(b.group.id);
  const isAFound = iOfA >= 0;
  const isBFound = iOfB >= 0;

  // If one of the groups couldn't be found, the found group always is sorted before
  // If both groups are NOT found, use normal string compare
  if (isAFound !== isBFound) {
    return isAFound ? -1 : 1;
  } else if (!isAFound && !isBFound) {
    // Both groups not found: compare strings
    const r = a.group.id.localeCompare(b.group.id);
    // If groups are identical, continue with usual behaviour
    if (r !== 0) {
      return r;
    }
  } else if (iOfA < iOfB) {
    return -1; // a before b
  } else if (a.group.id !== b.group.id) {
    return 1; // b before a
  }
  // groups are identical => compare feature labels

  return a.text.label.localeCompare(b.text.label);
}
